/* eslint-disable jsdoc/check-param-names */
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-param */
import React, { useState } from "react";
import {
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { bankAccountTypes } from "../../../../components/shared/constants";

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  infoItem: {
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
  },
  value: {
    color: theme.palette.text.secondary,
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  termsSection: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  submitButton: {
    minWidth: 200,
  },
}));

/**
 * Component to review the user's details before submitting the form.
 *
 * @param {Object} formData - The user's details to be reviewed.
 * @param {Function} onBack - Function to call when the user clicks the back button.
 * @param {Function} onSubmit - Function to call when the user clicks the submit button.
 * @param {boolean} loading - Is the form currently submitting.
 * @param {Function} updateFormData - Function to call when the user changes the terms and conditions checkbox.
 * @param {string} submitLabel - The label for the submit button. Defaults to "Submit".
 */
export default function ReviewStep({
  formData,
  onBack,
  onSubmit,
  loading,
  updateFormData,
  submitLabel = "Submit",
}) {
  const classes = useStyles();
  const [agreeToTerms, setAgreeToTerms] = useState(formData.TandCs || false);
  const [termsError, setTermsError] = useState(false);

  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  // Format phone for display
  const formatPhone = phone => {
    if (!phone) return "N/A";
    return phone;
  };

  // Handle terms change
  const handleTermsChange = event => {
    const { checked } = event.target;
    setAgreeToTerms(checked);
    setTermsError(false);
    updateFormData({ TandCs: checked });
  };

  // Handle submit
  const handleSubmit = () => {
    if (!agreeToTerms) {
      setTermsError(true);
      return;
    }
    onSubmit();
  };

  return (
    <Box>
      {/* Personal Information */}
      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.infoItem}>
                <span className={classes.label}>Name:</span>
                <span className={classes.value}>
                  {formData.firstName} {formData.middleName} {formData.surname}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Date of Birth:</span>
                <span className={classes.value}>
                  {formatDate(formData.dateOfBirth)}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Gender:</span>
                <span className={classes.value}>
                  {formData.refsGender === "M" ? "Male" : "Female"}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>ID Type:</span>
                <span className={classes.value}>
                  {formData.refsIdentityDocType === "RSAID"
                    ? "SA ID"
                    : "Passport"}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>ID Number:</span>
                <span className={classes.value}>
                  {formData.identificationNo}
                </span>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.infoItem}>
                <span className={classes.label}>Email:</span>
                <span className={classes.value}>{formData.email}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Phone:</span>
                <span className={classes.value}>
                  {formatPhone(formData.phone)}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Alt Phone:</span>
                <span className={classes.value}>
                  {formatPhone(formData.altPhone) || "N/A"}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Company:</span>
                <span className={classes.value}>
                  {formData.companyName || "N/A"}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>RICA Username:</span>
                <span className={classes.value}>
                  {formData.operatorId || "N/A"}
                </span>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Address Information */}
      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Address Information
          </Typography>
          <div className={classes.infoItem}>
            <span className={classes.label}>Street:</span>
            <span className={classes.value}>{formData.street}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Suburb:</span>
            <span className={classes.value}>{formData.suburb}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>City:</span>
            <span className={classes.value}>{formData.city}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Province:</span>
            <span className={classes.value}>{formData.province}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Postal Code:</span>
            <span className={classes.value}>{formData.postalCode}</span>
          </div>
        </CardContent>
      </Card>

      {/* Banking Information */}
      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Payment Information
          </Typography>
          <div className={classes.infoItem}>
            <span className={classes.label}>Payment Type:</span>
            <span className={classes.value}>
              {formData.refsPaymentType === "bank" ? "Bank Account" : "eWallet"}
            </span>
          </div>
          {formData.refsPaymentType === "bank" && (
            <>
              <div className={classes.infoItem}>
                <span className={classes.label}>Bank:</span>
                <span className={classes.value}>{formData.bank}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Account Type:</span>
                <span className={classes.value}>
                  {formData.refsBankAccountType &&
                    bankAccountTypes.filter(
                      item => item.value === formData.refsBankAccountType
                    )[0].label}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.label}>Account Number:</span>
                <span className={classes.value}>
                  {formData.bankAccountNumber}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Documents
          </Typography>
          <div className={classes.infoItem}>
            <span className={classes.label}>ID Photo:</span>
            <span className={classes.value}>
              {formData.idPhotoUrl ? "✓ Uploaded" : "✗ Not Uploaded"}
            </span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Person Photo:</span>
            <span className={classes.value}>
              {formData.personPhotoUrl ? "✓ Uploaded" : "✗ Not Uploaded"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Terms and Conditions */}
      <Box className={classes.termsSection}>
        {termsError && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            You must agree to the terms and conditions to proceed
          </Alert>
        )}
        <Typography
          style={{
            fontSize: "0.85rem",
            textOverflow: "ellipsis",
            paddingBottom: "10px",
          }}
        >
          Please note that Zawadi does not take any responsibility for incorrect
          bank details or cellphone numbers submitted. We will not be held
          accountable if your commission is not received due to these errors.
          Kindly double-check your details before submission to avoid any
          issues.
        </Typography>
        <Typography
          style={{
            fontSize: "0.85rem",
            textOverflow: "ellipsis",
            paddingBottom: "10px",
          }}
        >
          I confirm that I have trained the agent on the responsibilities of
          being a RICA and POPIA agent and the responsibilities as explained in
          the act.
        </Typography>
        <Grid direction="row">
          <a
            href="https://zmdjhwyplkwfgwmmtwak.supabase.co/storage/v1/object/public/terms_and_conditions/RICA_Terms_and_Conditions.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            RICA
          </a>
          {" , "}
          <a
            href="https://zmdjhwyplkwfgwmmtwak.supabase.co/storage/v1/object/public/terms_and_conditions/POPIA_Zawadi_mrg.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            POPIA
          </a>
        </Grid>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreeToTerms}
              onChange={handleTermsChange}
              color="primary"
            />
          }
          label="I agree to the terms and conditions"
        />
      </Box>

      <Divider />

      {/* Action Buttons */}
      <div className={classes.buttons}>
        <Button onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || !agreeToTerms}
          className={classes.submitButton}
        >
          {submitLabel}
        </Button>
      </div>
    </Box>
  );
}
